import { AuthConfig } from 'angular-oauth2-oidc';
import { environment } from '../../environments/environment';
import { FeatureFlagsEnum } from '../feature-flag-constant';
// ===== B2C ( provider) =====
const domain = environment.b2cDomain;
const tenantName = `${domain}.onmicrosoft.com`;
const apiScope = `https://${tenantName}/${environment.b2cBeScope}/access_as_user`;

export const b2cConfig: AuthConfig = {
  //  Use the policy path (the lib will fetch `${issuer}/.well-known/openid-configuration`)

  clientId: environment.b2cClientId,
  redirectUri: `${window.location.origin}/auth/b2c`,
  postLogoutRedirectUri: `${window.location.origin}/`,
  responseType: 'code',
  scope: `openid profile email offline_access ${apiScope}`,

  //  Avoid the GUID vs policy issuer mismatch
  strictDiscoveryDocumentValidation: false,
  skipIssuerCheck: true,
  disableAtHashCheck: true,
  clearHashAfterLogin: true,
  sessionChecksEnabled: false,
  showDebugInformation: false,
};
export const b2cSigninConfig: AuthConfig = {
  ...b2cConfig,
  issuer: `https://${environment.b2cDomain}.b2clogin.com/${environment.b2cDomain}.onmicrosoft.com/B2C_1_auth/v2.0`,
};
export const b2cResetPasswordConfig: AuthConfig = {
  ...b2cConfig,
  issuer: `https://${environment.b2cDomain}.b2clogin.com/${environment.b2cDomain}.onmicrosoft.com/B2C_1_resetpassword/v2.0`,
};

export const b2cEditProfileConfig: AuthConfig = {
  ...b2cConfig,
  issuer: `https://${environment.b2cDomain}.b2clogin.com/${environment.b2cDomain}.onmicrosoft.com/B2C_1_editprofile_raultest/v2.0`,
};
// ===== B2C ( end  ) =====

// ===== Entra (new provider) =====
const entraIssuer = `https://${environment.entraDomain}/${environment.entraTenantId}/v2.0`;

export const entraSignInConfig: AuthConfig = {
  issuer: entraIssuer,
  clientId: environment.entraClientId,
  redirectUri: `${window.location.origin}/auth/entra`,
  postLogoutRedirectUri: `${window.location.origin}/`,
  responseType: 'code',
  scope: `openid profile email offline_access ${apiScope}`,

  // For Entra (standard OIDC), keep strict checks ON
  strictDiscoveryDocumentValidation: false,
  skipIssuerCheck: false,
  useSilentRefresh: false,
  disableAtHashCheck: true,
  clearHashAfterLogin: true,
  sessionChecksEnabled: false,
  showDebugInformation: false,
};
// ===== Entra ( end  ) =====

// ===== Provider registry =====
export type ProviderKey = 'b2c' | 'entra';
export type B2CPolicy = 'signin' | 'reset' | 'edit';
export type AuthProviderPolicy = 'signin' | 'reset' | 'edit';

type AuthProviderDefinition = {
  key: ProviderKey;
  defaultPolicy: AuthProviderPolicy;
  supportedPolicies: readonly AuthProviderPolicy[];
  buildConfig: (policy: AuthProviderPolicy) => AuthConfig;
};

export type AuthProviderLoginOption = {
  key: ProviderKey;
  title: string;
  logoSrc: string;
  logoAlt: string;
  featureFlag?: FeatureFlagsEnum;
};

const B2C_POLICY_CONFIGS: Record<B2CPolicy, AuthConfig> = {
  signin: b2cSigninConfig,
  reset: b2cResetPasswordConfig,
  edit: b2cEditProfileConfig,
};

function buildB2CConfig(policy: AuthProviderPolicy): AuthConfig {
  const resolvedPolicy: B2CPolicy =
    policy === 'reset' || policy === 'edit' ? policy : 'signin';
  const cfg = B2C_POLICY_CONFIGS[resolvedPolicy];
  return { ...cfg };
}

export const AUTH_PROVIDERS: Record<ProviderKey, AuthProviderDefinition> = {
  b2c: {
    key: 'b2c',
    defaultPolicy: 'signin',
    supportedPolicies: ['signin', 'reset', 'edit'],
    buildConfig: (policy) => buildB2CConfig(policy),
  },
  entra: {
    key: 'entra',
    defaultPolicy: 'signin',
    supportedPolicies: ['signin'],
    buildConfig: () => ({ ...entraSignInConfig }),
  },
};

export const DEFAULT_PROVIDER: ProviderKey = 'b2c';
// UI list for login page. Add new providers here once added to AUTH_PROVIDERS.
export const AUTH_LOGIN_PROVIDERS: readonly AuthProviderLoginOption[] = [
  {
    key: 'b2c',
    title: 'Login',
    logoSrc: 'assets/svg/azure.svg',
    logoAlt: 'Azure AD B2C Logo',
  },
  {
    key: 'entra',
    title: 'Continue with QS ID',
    logoSrc: 'assets/svg/logo_bubble.svg',
    logoAlt: 'Microsoft Entra ID Logo',
    featureFlag: FeatureFlagsEnum.ENTRA_LOGIN,
  },
];

export function isProviderKey(
  value: string | null | undefined,
): value is ProviderKey {
  return value === 'b2c' || value === 'entra';
}

export function getDefaultPolicy(provider: ProviderKey): AuthProviderPolicy {
  return AUTH_PROVIDERS[provider].defaultPolicy;
}

export function supportsPolicy(
  provider: ProviderKey,
  policy: AuthProviderPolicy,
): boolean {
  return AUTH_PROVIDERS[provider].supportedPolicies.includes(policy);
}

export function getProviderConfig(
  provider: ProviderKey,
  policy: AuthProviderPolicy,
): AuthConfig {
  return AUTH_PROVIDERS[provider].buildConfig(policy);
}

export function resolveProviderFromAuthRoute(url: string): ProviderKey | null {
  const routePath = url.split('?')[0].split('#')[0];
  const segments = routePath.split('/').filter(Boolean);
  if (segments[0] !== 'auth') {
    return null;
  }
  const candidate = segments[1];
  return isProviderKey(candidate) ? candidate : null;
}

export const protectedResources = {
  apiTodoList: {
    endpoint: environment.fleetApiUrl,
    // The scopes below are no longer used by the interceptor,
    // but keeping them here avoids breaking imports and may be useful elsewhere.
    scopes: {
      read: [
        `https://${environment.b2cDomain}.onmicrosoft.com/${environment.b2cBeScope}/access_as_user`,
      ],
      write: [
        `https://${environment.b2cDomain}.onmicrosoft.com/${environment.b2cBeScope}/access_as_user`,
      ],
    },
  },
};
